import React, { useMemo, useState } from "react";
import { Box, Paper, Snackbar, Alert, CircularProgress } from "@mui/material";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useManifiestos, usePasajeros, useAsignaciones, useRutasManifiesto } from "../../../hooks/useManifest";
import { useViajeros } from "../../../hooks/administration/useServiceDetails";
import { usePersons } from "../../../hooks/usePersons";
import { useAuthContext } from "../../../context/AuthProvider";

import PassengerForm from "./components/PassengerForm";
import ManifestAssignRouteForm from "./components/route/ManifestAssignRouteForm";
import AssignForm from "./components/AssignForm";
import TravelerSelector from "../serviceOrders/TravelerSelector";

import ManifestHeader from "./components/ManifestHeader";
import ManifestStatusActions from "./components/ManifestStatusActions";
import ManifestRouteSummary from "./components/ManifestRouteSummary";
import ManifestAssignmentsPanel from "./components/ManifestAssignmentsPanel";
import PassengerScanner from "./components/PassengerScanner";
import ManifestPassengersTable from "./components/ManifestPassengersTable";
import PdfPreviewModal from "./components/PdfPreviewModal";

const ManifestDetail = () => {
    const queryClient = useQueryClient();
    const { manifestId } = useParams();
    const manifestIdNum = Number(manifestId);
    const { user } = useAuthContext();

    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [pdfUrl, setPdfUrl] = useState(null);
    const [openPdfModal, setOpenPdfModal] = useState(false);

    const [openAssignForm, setOpenAssignForm] = useState(false);
    const [assignType, setAssignType] = useState(null);
    const [openAssignRoute, setOpenAssignRoute] = useState(false);
    const [openTravelerSelector, setOpenTravelerSelector] = useState(false);

    const [openPassengerForm, setOpenPassengerForm] = useState(false);
    const [editPassenger, setEditPassenger] = useState(null);

    const [dniToSearch, setDniToSearch] = useState("");
    const [scannerResult, setScannerResult] = useState({
        open: false,
        passenger: null,
        traveler: null,
        person: null,
    });

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const {
        useFetchManifiesto,
        printManifestPDF,
        printRoadMapPDF,
        printManifestPDFSF,
        printManifestRoadSheet10,
        updateEstadoMutation,
    } = useManifiestos(manifestId);

    const { data: manifest, isLoading, isError } = useFetchManifiesto(manifestId);

    const {
        assignEmployeeMutation,
        removeEmployeeMutation,
        assignVehicleMutation,
        removeVehicleMutation,
    } = useAsignaciones(manifestId);

    const {
        paradas: paradasActuales,
        assignRuta,
    } = useRutasManifiesto(manifestId);

    const {
        pasajeros: passengers,
        refetch: refetchPassengers,
        addMutation: addPassenger,
        updateMutation: updatePassenger,
        deleteMutation: deletePassenger,
        isLoading: loadingPassengers,
    } = usePasajeros(manifestIdNum);

    //const { useFetchPersonsByDNI } = usePersons();
    //const { data: personSearch, refetch: refetchPersonByDni } = useFetchPersonsByDNI(dniToSearch, { enabled: false });

    const serviceOrderId = manifest?.orden_servicio_manifiesto?.OrdSerCabID;
    const tramo = manifest?.orden_servicio_manifiesto?.OrdSerManTra;

    const {
        viajeros: travelers,
        refetch: refetchTravelers,
    } = useViajeros(serviceOrderId);

    const origen = paradasActuales?.[0] || null;
    const destino = paradasActuales?.at?.(-1) || null;

    const roleIds = useMemo(() => ({
        admin: Number(process.env.REACT_APP_ROL_ADMIN_ID),
        administrador: Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID),
        operaciones: Number(process.env.REACT_APP_ROL_OPERACIONES_ID),
        conductor: Number(process.env.REACT_APP_ROL_CONDUCTOR_ID),
    }), []);

    const permissions = useMemo(() => {
        const isOperations = user?.RolID === roleIds.operaciones;
        const isDriver = user?.RolID === roleIds.conductor;
        const isAdmin = user?.RolID === roleIds.admin;

        const isAssignedDriver = manifest?.empleados?.some((e) => Number(e.EmpID) === Number(user?.EmpID));

        return {
            canEditSetup: isOperations && manifest?.manest === "CREADO",
            canPlan: isOperations && manifest?.manest === "CREADO",
            canStart: (isOperations && manifest?.manest === "PLANIFICADO") || (isDriver && isAssignedDriver && manifest?.manest === "PLANIFICADO"),
            canComplete: (isOperations && manifest?.manest === "EN_PROCESO") || (isDriver && isAssignedDriver && manifest?.manest === "EN_PROCESO"),
            canCancel: isAdmin,
            canScanPassengers: isDriver && isAssignedDriver && manifest?.manest === "EN_PROCESO",
        };
    }, [user, roleIds, manifest]);

    const viajerosAsociados = useMemo(() => {
        return travelers?.filter((t) => t.ordserdettra === tramo) || [];
    }, [travelers, tramo]);

    if (isLoading) return <CircularProgress />;
    if (isError || !manifest) return <Alert severity="error">No se pudo cargar el manifiesto.</Alert>;

    const openPdf = async (fn) => {
        const url = await fn(manifestId);
        if (!url) {
            showAlert("No se pudo generar el PDF.", "error");
            return;
        }
        setPdfUrl(url);
        setOpenPdfModal(true);
    };

    const closePdf = () => {
        if (pdfUrl) URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setOpenPdfModal(false);
    };

    const handleAssign = (data) => {
        if (assignType === "employee") {
            assignEmployeeMutation.mutate(
                {
                    empid: data.id,
                    isinv: data.isInvitado,
                    proid: manifest.proid,
                    manfecfin: manifest.manfecfin,
                    updbyid: user.EmpID,
                },
                {
                    onSuccess: (response) => {
                        setOpenAssignForm(false);
                        showAlert(response.message || "Conductor asignado correctamente.");
                    },
                    onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al asignar conductor.", "error"),
                }
            );
            return;
        }

        assignVehicleMutation.mutate(
            {
                vehid: data,
                vehisinv: data.isInvitado,
                proid: manifest.proid,
                manfecfin: manifest.manfecfin,
                updbyid: user.EmpID,
            },
            {
                onSuccess: (response) => {
                    setOpenAssignForm(false);
                    showAlert(response.message || "Vehículo asignado correctamente.");
                },
                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al asignar vehículo.", "error"),
            }
        );
    };

    const handleAssignRoute = (payload) => {
        assignRuta.mutate(
            { manifiestoId: manifestId, payload },
            {
                onSuccess: () => {
                    setOpenAssignRoute(false);
                    showAlert("Ruta asignada o actualizada correctamente.");
                },
                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al actualizar ruta.", "error"),
            }
        );
    };

    const validateBeforeStateChange = (nextState) => {
        if (manifest.manest !== "CREADO" || nextState !== "PLANIFICADO") return true;

        const errores = [];

        if (!Array.isArray(paradasActuales) || paradasActuales.length < 2) {
            errores.push("Debe asignar al menos 2 paradas.");
        }

        if (paradasActuales?.some((p) => !p.parhorest)) {
            errores.push("Todas las paradas deben tener fecha y hora.");
        }

        if (!manifest.empleados?.length) {
            errores.push("Debe asignar al menos un conductor.");
        }

        if (!manifest.vehid) {
            errores.push("Debe asignar un vehículo.");
        }

        if (errores.length) {
            showAlert(errores.join(" "), "error");
            return false;
        }

        return true;
    };

    const handleChangeState = async ({ nextState, observation }) => {
        if (!validateBeforeStateChange(nextState)) {
            throw new Error("El manifiesto no cumple las condiciones requeridas.");
        }

        try {
            await updateEstadoMutation.mutateAsync({
                id: manifest.manid,
                data: {
                    manest: nextState,
                    updbyid: user.EmpID,
                    ordsermanobs:
                        nextState === "COMPLETADO"
                            ? observation
                            : null,
                },
            });

            if (nextState === "COMPLETADO") {
                await refetchPassengers();
            }

            showAlert("Estado actualizado correctamente.");
        } catch (error) {
            showAlert(
                error.response?.data?.error ||
                error.message ||
                "Error al cambiar el estado.",
                "error"
            );

            throw error;
        }
    };

    const handleSearchPassenger = async () => {
        if (!dniToSearch || dniToSearch.length !== 8) {
            showAlert("Ingrese un DNI válido de 8 dígitos.", "warning");
            return;
        }

        const passenger = passengers?.find((p) => p.perdni === dniToSearch);
        if (passenger) {
            setScannerResult({ open: true, passenger, traveler: null, person: null });
            return;
        }

        const traveler = viajerosAsociados?.find((t) => t.perdni === dniToSearch);
        if (traveler) {
            setScannerResult({ open: true, passenger: null, traveler, person: null });
            return;
        }

        //const { data } = await refetchPersonByDni();
        //setScannerResult({ open: true, passenger: null, traveler: null, person: data || null });
    };

    const handleConfirmPassenger = (passenger) => {
        updatePassenger.mutate(
            {
                manifiestoDetalleId: passenger.mandetid,
                data: {
                    ...passenger,
                    mandetestpas: "CONFIRMADO",
                    updbyid: user.EmpID,
                    manid: manifestId,
                },
            },
            {
                onSuccess: () => {
                    setScannerResult({ open: false, passenger: null, traveler: null, person: null });
                    setDniToSearch("");
                    showAlert("Pasajero confirmado correctamente.");
                },
                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al confirmar pasajero.", "error"),
            }
        );
    };

    const handleConfirmTraveler = (traveler) => {
        addPassenger.mutate(
            {
                manid: manifestIdNum,
                perid: traveler.perid,
                mandetpunsub: traveler.puntosubida || "",
                mandetpunbaj: traveler.puntobajada || "",
                mandettaxser: traveler.ordserdettaxser || false,
                mandetestpas: "CONFIRMADO",
                crebyid: user.EmpID,
                ordserdetid: traveler.ordserdetid,
                ordserdetestpas: "ASIGNADO",
            },
            {
                onSuccess: () => {
                    setScannerResult({ open: false, passenger: null, traveler: null, person: null });
                    setDniToSearch("");
                    showAlert("Viajero asignado y confirmado correctamente.");
                },
                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al registrar viajero.", "error"),
            }
        );
    };

    const handleRegisterPerson = (person) => {
        addPassenger.mutate(
            {
                perdni: dniToSearch,
                perid: person?.perid,
                pernom: person?.pernom || "Nuevo Pasajero",
                perape: person?.perape || "",
                mandetpunsub: manifest?.manori || "",
                mandetpunbaj: manifest?.mandes || "",
                mandetestpas: "CONFIRMADO",
                crebyid: user.EmpID,
                manid: manifestId,
                ordsercabid: serviceOrderId,
                ordsermantra: tramo,
                ordserdetestpas: "INVITADO",
                mandetpunsubid: origen?.locid,
                mandetpunbajid: destino?.locid,
            },
            {
                onSuccess: () => {
                    setScannerResult({ open: false, passenger: null, traveler: null, person: null });
                    setDniToSearch("");
                    showAlert("Pasajero invitado registrado correctamente.");
                },
                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al registrar pasajero.", "error"),
            }
        );
    };

    const handleSelectTravelers = async (selectedTravelers, assignedOriginals) => {
        const nuevos = selectedTravelers.filter((t) => !t.mandetid);
        const eliminados = assignedOriginals.filter(
            (o) => !selectedTravelers.some((t) => t.ordserdetid === o.ordserdetid)
        );

        try {
            await Promise.all([
                ...nuevos.map((t) =>
                    addPassenger.mutateAsync({
                        manid: manifestIdNum,
                        perid: t.perid,
                        mandetpunsub: t.puntosubida || "",
                        mandetpunbaj: t.puntobajada || "",
                        mandettaxser: t.ordserdettaxser || false,
                        mandetestpas: "RESERVADO",
                        crebyid: user.EmpID,
                        ordserdetid: t.ordserdetid,
                        ordserdetestpas: "ASIGNADO",
                    })
                ),
                ...eliminados.map((t) =>
                    deletePassenger.mutateAsync({
                        manifiestoDetalleId: t.mandetid,
                        data: {
                            perid: t.perid,
                            manifestId: manifestIdNum,
                            ordserdetid: t.ordserdetid,
                        },
                    })
                ),
            ]);

            showAlert("Pasajeros actualizados correctamente.");
            refetchTravelers();
        } catch (error) {
            showAlert(error.response?.data?.error || error.message || "Error al actualizar pasajeros.", "error");
        } finally {
            setOpenTravelerSelector(false);
        }
    };

    return (
        <Paper variant="outlined" sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            height: "calc(100vh - 120px)",
            overflow: "hidden"
        }}>
            <Box
                display="flex"
                flexDirection="column"
                gap={2.5}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: "auto"
                }}
            >
                <ManifestHeader
                    manifest={manifest}
                    manifestId={manifestId}
                    onPrintManifest={() => openPdf(printManifestPDF)}
                    onPrintManifestSF={() => openPdf(printManifestPDFSF)}
                    onPrintRoadMap={() => openPdf(printRoadMapPDF)}
                    onPrintRoadSheet10={() => openPdf(printManifestRoadSheet10)}
                />

                <ManifestStatusActions
                    manifest={manifest}
                    permissions={permissions}
                    loading={updateEstadoMutation.isPending}
                    onChangeState={handleChangeState}
                />

                {permissions.canScanPassengers && (
                    <PassengerScanner
                        dni={dniToSearch}
                        setDni={setDniToSearch}
                        result={scannerResult}
                        setResult={setScannerResult}
                        onSearch={handleSearchPassenger}
                        onConfirmPassenger={handleConfirmPassenger}
                        onConfirmTraveler={handleConfirmTraveler}
                        onRegisterPerson={handleRegisterPerson}
                    />
                )}

                <ManifestRouteSummary
                    manifest={manifest}
                    origen={origen}
                    destino={destino}
                    canEdit={permissions.canEditSetup}
                    onEditRoute={() => setOpenAssignRoute(true)}
                />

                <ManifestAssignmentsPanel
                    manifest={manifest}
                    canEdit={permissions.canEditSetup}
                    onAssignEmployee={() => {
                        setAssignType("employee");
                        setOpenAssignForm(true);
                    }}
                    onAssignVehicle={() => {
                        setAssignType("vehicle");
                        setOpenAssignForm(true);
                    }}
                    onRemoveEmployee={(empid) =>
                        removeEmployeeMutation.mutate(
                            { empid, updbyid: user.EmpID },
                            {
                                onSuccess: () => showAlert("Conductor retirado correctamente."),
                                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al retirar conductor.", "error"),
                            }
                        )
                    }
                    onRemoveVehicle={() =>
                        removeVehicleMutation.mutate(
                            { updbyid: user.EmpID },
                            {
                                onSuccess: () => showAlert("Vehículo retirado correctamente."),
                                onError: (error) => showAlert(error.response?.data?.error || error.message || "Error al retirar vehículo.", "error"),
                            }
                        )
                    }
                />

                <ManifestPassengersTable
                    passengers={passengers || []}
                    loading={loadingPassengers}
                    canSelectPassengers={permissions.canEditSetup}
                    onOpenSelector={() => setOpenTravelerSelector(true)}
                />
            </Box>

            {openAssignForm && (
                <AssignForm
                    open={openAssignForm}
                    manifestId={manifestId}
                    manifestproid={manifest.manid}
                    manifestfecfin={manifest.manfecfin}
                    handleClose={() => setOpenAssignForm(false)}
                    onAssign={handleAssign}
                    type={assignType}
                />
            )}

            {openAssignRoute && (
                <ManifestAssignRouteForm
                    open={openAssignRoute}
                    handleClose={() => setOpenAssignRoute(false)}
                    manifest={manifest}
                    updByID={user?.EmpID}
                    onAssigned={handleAssignRoute}
                    defaultParadas={paradasActuales}
                />
            )}

            {openTravelerSelector && (
                <TravelerSelector
                    open={openTravelerSelector}
                    handleClose={() => setOpenTravelerSelector(false)}
                    ordenDetalle={viajerosAsociados}
                    viajerosAsociados={passengers}
                    manifiesto={manifest}
                    trayecto={tramo}
                    onSubmit={handleSelectTravelers}
                />
            )}

            {openPassengerForm && (
                <PassengerForm
                    open={openPassengerForm}
                    handleClose={() => {
                        setOpenPassengerForm(false);
                        setEditPassenger(null);
                    }}
                    defaultValues={editPassenger || {}}
                />
            )}

            <PdfPreviewModal open={openPdfModal} pdfUrl={pdfUrl} onClose={closePdf} />

            <Snackbar
                open={alert.open}
                autoHideDuration={4500}
                onClose={() => setAlert((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert severity={alert.severity} onClose={() => setAlert((prev) => ({ ...prev, open: false }))}>
                    {alert.message}
                </Alert>
            </Snackbar>
        </Paper>
    );
};

export default ManifestDetail;